import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import privateCampClearing from '../../../assets/camp/private-camp-clearing.webp';
import campCablewayIcon from '../../../assets/icons/camp-cableway.svg';
import { MEDIA_VARIANTS } from '../../../media';
import Button from '../../../ui/Button';
import { GameCrafting } from '../Game_Crafting';
import { ImageLoader } from '../ImageLoader';
import ConsumableEffectsHud from '../Home/ConsumableEffectsHud';
import { OperativeStatusButton } from '../Home/StatusBar';
import CampProjectsPanel from './CampProjectsPanel';
import { CampStorageWorkspace } from './CampStorageDialog';
import ProfessionProgress from './ProfessionProgress';
import TradingPost from '../Market/TradingPost';
import './BaseCampScene.css';

const UTILITY_SERVICES = [
  {
    key: 'character',
    label: 'Character',
    eyebrow: 'Operative overview',
    description: 'Review your operative, stats, equipment, and Pack.',
    kind: 'character',
  },
  {
    key: 'projects',
    label: 'Projects',
    eyebrow: 'Camp management',
    description: 'Restore and improve Camp services.',
    kind: 'projects',
  },
  {
    key: 'travel',
    label: 'Travel',
    eyebrow: 'Camp cableway',
    description: 'Travel to regions connected to your Camp.',
    kind: 'travel',
  },
];

const SPECIAL_BUILDING_SERVICES = {
  found_vault: {
    serviceKey: 'vault',
    kind: 'storage',
  },
  sleeping_place: {
    serviceKey: 'sleeping',
    kind: 'rest',
  },
  workbench: {
    serviceKey: 'workbench',
    kind: 'crafting',
    stationKey: 'workbench',
  },
  cooking_place: {
    serviceKey: 'cooking',
    kind: 'crafting',
    stationKey: 'cooking_place',
  },
  trading_post: {
    kind: 'market',
  },
};

export function getBaseCampServices(baseCamp) {
  const buildingServices = (baseCamp?.buildings || []).flatMap((
    building,
  ) => {
    const special = SPECIAL_BUILDING_SERVICES[building.buildingKey] || {};
    const stationKey = building.craftingStationKey
      || special.stationKey
      || '';
    const kind = special.kind || (stationKey ? 'crafting' : 'info');
    const configuredLevel = Number(
      building.currentLevel ?? building.CurrentLevel,
    );
    const completed = (
      (Number.isFinite(configuredLevel) && configuredLevel > 0)
      || building.status === 'completed'
    );
    const unlocked = kind === 'storage'
      ? Boolean(baseCamp?.storage?.unlocked || completed)
      : completed;
    const service = {
      key: special.serviceKey || `building:${building.buildingKey}`,
      label: building.name || building.buildingKey,
      eyebrow: {
        crafting: 'Camp workshop',
        info: 'Camp service',
        market: 'Player market',
        rest: 'Recovery service',
        storage: 'Run-safe Vault',
      }[kind] || 'Camp service',
      description: building.description || 'A completed Camp building.',
      kind,
      building,
      buildingKey: building.buildingKey,
      stationKey,
      professionKey: building.professionKey || '',
      imageId: building.imageId || '',
      unlocked,
      lockedReason: unlocked
        ? ''
        : `Complete the ${building.name || building.buildingKey} project to unlock this service.`,
    };
    return building.buildingKey === 'workbench'
      ? [service, {
        ...service,
        key: 'salvage',
        label: 'Salvage Station',
        eyebrow: 'Recovery workshop',
        description: 'Break down unwanted equipment into recovered materials and item knowledge.',
        kind: 'salvage',
        professionKey: 'salvage',
      }]
      : [service];
  });
  return [...UTILITY_SERVICES.map(service => ({
    ...service,
    unlocked: true,
    lockedReason: '',
  })), ...buildingServices];
}

function SleepingPlace({
  currentAction,
  isRespawnPoint,
  onRequestRest,
  onSetRespawnPoint,
  respawnBusy,
  respawnRegionId,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const requestRest = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (!onRequestRest) {
        throw new Error('Resting is not available right now.');
      }
      await onRequestRest();
      setMessage('Resting requested. Recovery continues while you remain here.');
    } catch (restError) {
      setError(
        restError?.message || 'Resting could not be requested. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const setRespawnPoint = async () => {
    if (respawnBusy || isRespawnPoint) return;
    setError('');
    setMessage('');
    try {
      if (!onSetRespawnPoint) {
        throw new Error('Changing the respawn point is not available right now.');
      }
      const result = await onSetRespawnPoint();
      if (result) setMessage('This Camp is now your respawn point.');
    } catch (respawnError) {
      setError(
        respawnError?.message || 'The respawn point could not be changed.',
      );
    }
  };

  return (
    <section
      aria-label="Sleeping Place controls"
      className="base-camp-service"
    >
      <dl className="base-camp-service__facts">
        <div>
          <dt>Current action</dt>
          <dd>{currentAction || 'IDLE'}</dd>
        </div>
        <div>
          <dt>Recovery</dt>
          <dd>Camp rest bonus active</dd>
        </div>
        <div>
          <dt>Respawn point</dt>
          <dd>
            {isRespawnPoint
              ? 'This Camp'
              : respawnRegionId
                ? 'Another Camp'
                : 'Amnesia Cove'}
          </dd>
        </div>
      </dl>
      {error ? <p className="base-camp-service__error" role="alert">{error}</p> : null}
      <p aria-live="polite" className="base-camp-service__status" role="status">
        {busy ? 'Requesting rest…' : message}
      </p>
      <Button disabled={busy} onClick={requestRest} variant="primary">
        {busy ? 'Requesting…' : 'Rest and recover'}
      </Button>
      <Button
        disabled={respawnBusy || isRespawnPoint}
        onClick={setRespawnPoint}
        variant="secondary"
      >
        {respawnBusy
          ? 'Setting respawn point…'
          : isRespawnPoint
            ? 'Current respawn point'
            : 'Set as respawn point'}
      </Button>
    </section>
  );
}

function CharacterService({ character }) {
  return (
    <section
      aria-label="Character details"
      className="base-camp-service base-camp-character"
      id="base-camp-character-panel"
    >
      {character ? (
        <div className="base-camp-character__grid">
          <section
            aria-labelledby="base-camp-character-overview-title"
            className="base-camp-character__card base-camp-character__card--overview"
          >
            <h3 id="base-camp-character-overview-title">Status &amp; Stats</h3>
            {character.overview}
          </section>
          <section
            aria-labelledby="base-camp-character-equipment-title"
            className="base-camp-character__card base-camp-character__card--equipment"
          >
            <h3 id="base-camp-character-equipment-title">Equipment</h3>
            {character.equipment}
          </section>
          <section
            aria-labelledby="base-camp-character-pack-title"
            className="base-camp-character__card base-camp-character__card--pack"
          >
            <h3 id="base-camp-character-pack-title">Pack</h3>
            {character.pack}
          </section>
        </div>
      ) : (
        <p className="base-camp-character__empty" role="status">
          Character data is not available yet.
        </p>
      )}
    </section>
  );
}

function TravelService({
  onRequestRoute,
  routes,
  travelBusy,
}) {
  return (
    <section aria-label="Unlocked Camp cableways" className="base-camp-service base-camp-travel">
      <header className="base-camp-travel__heading">
        <h3>Unlocked destinations</h3>
        <span>{routes.length} connected</span>
      </header>
      {routes.length ? (
        <ul className="base-camp-travel__routes">
          {routes.map(route => {
            const destination = route.regionName || 'Unknown region';
            const action = route.isCurrentRegion ? 'Return' : 'Travel';
            return (
              <li data-current={route.isCurrentRegion || undefined} key={route.regionId}>
                <span aria-hidden="true" className="base-camp-travel__icon">
                  <img alt="" src={campCablewayIcon} />
                </span>
                <span className="base-camp-travel__destination">
                  <strong>{destination}</strong>
                  <small>
                    Region level {route.regionLevel || 1}
                    {route.isCurrentRegion ? ' · Last location' : ''}
                  </small>
                </span>
                <Button
                  aria-label={`${action} to ${destination} via Camp cableway`}
                  disabled={travelBusy}
                  onClick={() => onRequestRoute?.(route)}
                  variant={route.isCurrentRegion ? 'primary' : 'secondary'}
                >
                  {travelBusy ? 'Travelling…' : action}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="base-camp-travel__empty" role="status">
          No cableway destinations are connected yet.
        </p>
      )}
    </section>
  );
}

export default function BaseCampScene({
  baseCamp,
  busyBuildingKey = '',
  character = null,
  currentAction = '',
  error = '',
  headerMenu = null,
  inventory = [],
  loading = false,
  onCancel,
  onCraftMutation,
  onDeposit,
  onRefresh,
  onMarketMutation,
  onRequestRoute,
  onRequestRest,
  onSalvage,
  onSetRespawnPoint,
  onStart,
  onTransferStorage,
  storageBusy = false,
  respawnBusy = false,
  travelBusy = false,
}) {
  const services = useMemo(() => getBaseCampServices(baseCamp), [baseCamp]);
  const installedRoutes = useMemo(() => (baseCamp?.routes || [])
    .filter(route => route.installed)
    .sort((left, right) => (
      Number(left.regionLevel || 0) - Number(right.regionLevel || 0)
      || String(left.regionName || '').localeCompare(
        String(right.regionName || ''),
      )
    )), [baseCamp?.routes]);
  const [activeServiceKey, setActiveServiceKey] = useState('character');
  const contentRef = useRef(null);
  const activeService = services.find(service => (
    service.key === activeServiceKey
  )) || services[0];
  const currentRoute = installedRoutes.find(route => route.isCurrentRegion);
  const activeProject = baseCamp?.buildings?.find(building => (
    building.status === 'building'
  ));
  const completedBuildings = (baseCamp?.buildings || []).filter(building => (
    Number(building.currentLevel ?? building.CurrentLevel) > 0
    || building.status === 'completed'
  )).length;
  const contextualProgress = activeService.kind === 'character' && character
    ? {
      experience: Number(character.player?.xp) || 0,
      level: character.level,
      levelStartExperience: Number(character.currentLevelXp) || 0,
      name: character.player?.name || 'Operative',
      nextLevelExperience: Number(character.xpForNextLevel) || null,
    }
    : (baseCamp?.professions || []).find(profession => (
      profession.professionKey === activeService.professionKey
    ));
  const contextSummary = (() => {
    if (activeService.kind === 'character') {
      return `Operative level ${Math.max(1, Number(character?.level) || 1)}`;
    }
    if (activeService.kind === 'projects') {
      return activeProject
        ? `Building ${activeProject.name || activeProject.buildingKey}`
        : `${completedBuildings} of ${baseCamp?.buildings?.length || 0} services restored`;
    }
    if (activeService.kind === 'travel') {
      return `${installedRoutes.length} destination${installedRoutes.length === 1 ? '' : 's'} connected`;
    }
    if (activeService.kind === 'storage') {
      return `${baseCamp?.storage?.entries?.length || 0} of ${baseCamp?.storage?.capacity || 0} slots used`;
    }
    if (activeService.kind === 'rest') {
      return baseCamp?.isRespawnPoint
        ? 'Current respawn point'
        : 'Respawn point not set here';
    }
    if (activeService.building) {
      const configuredLevel = Number(activeService.building.currentLevel
        ?? activeService.building.CurrentLevel);
      const currentLevel = Number.isFinite(configuredLevel)
        ? Math.max(0, configuredLevel)
        : activeService.building.status === 'completed' ? 1 : 0;
      const maximumLevel = Math.max(
        currentLevel,
        Number(activeService.building.maxLevel
          ?? activeService.building.MaxLevel) || currentLevel,
      );
      return `Building level ${currentLevel}${maximumLevel ? ` of ${maximumLevel}` : ''}`;
    }
    return 'Camp service ready';
  })();

  useEffect(() => {
    if (activeService?.unlocked) return;
    setActiveServiceKey('character');
  }, [activeService?.unlocked]);

  const revealActiveService = ({ focusContent = true } = {}) => {
    const reveal = () => {
      const reducedMotion = (
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
      if (focusContent) {
        contentRef.current?.focus({ preventScroll: true });
      }
      contentRef.current?.scrollIntoView?.({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    };
    if (
      typeof window !== 'undefined'
      && typeof window.requestAnimationFrame === 'function'
    ) {
      window.requestAnimationFrame(reveal);
      return;
    }
    reveal();
  };

  const selectService = (
    service,
    { focusContent = true, reveal = false } = {},
  ) => {
    if (!service?.unlocked) return;
    setActiveServiceKey(service.key);
    if (reveal) revealActiveService({ focusContent });
  };

  const content = (() => {
    if (activeService.kind === 'character') {
      return <CharacterService character={character} />;
    }
    if (activeService.kind === 'storage') {
      return (
        <section
          aria-label="Vault"
          className="base-camp-service base-camp-service--storage"
        >
          <CampStorageWorkspace
            busy={storageBusy}
            error={error}
            inventory={inventory}
            loading={loading}
            onRefresh={onRefresh}
            onTransfer={onTransferStorage}
            storage={baseCamp?.storage}
          />
        </section>
      );
    }
    if (activeService.kind === 'rest') {
      return (
        <SleepingPlace
          currentAction={currentAction}
          isRespawnPoint={baseCamp?.isRespawnPoint}
          onRequestRest={onRequestRest}
          onSetRespawnPoint={onSetRespawnPoint}
          respawnBusy={respawnBusy}
          respawnRegionId={baseCamp?.respawnRegionId}
        />
      );
    }
    if (activeService.kind === 'salvage') {
      return (
        <section className="base-camp-service base-camp-service--crafting">
          <CampStorageWorkspace
            busy={storageBusy}
            error={error}
            inventory={inventory}
            loading={loading}
            mode="salvage"
            onRefresh={onRefresh}
            onSalvage={onSalvage}
            onTransfer={onTransferStorage}
            storage={baseCamp?.storage}
          />
        </section>
      );
    }
    if (activeService.kind === 'crafting') {
      return (
        <div className="base-camp-service base-camp-service--crafting">
          <GameCrafting
            baseCamp={baseCamp}
            inventory={inventory}
            onMutation={onCraftMutation}
            professionKey={activeService.professionKey}
            stationKey={activeService.stationKey}
            stationName={activeService.label}
          />
        </div>
      );
    }
    if (activeService.kind === 'market') {
      return (
        <div className="base-camp-service base-camp-service--market">
          <TradingPost
            baseCamp={baseCamp}
            inventory={inventory}
            onMutation={onMarketMutation}
          />
        </div>
      );
    }
    if (activeService.kind === 'travel') {
      return (
        <TravelService
          onRequestRoute={onRequestRoute}
          routes={installedRoutes}
          travelBusy={travelBusy}
        />
      );
    }
    if (activeService.kind === 'info') {
      return (
        <section
          aria-label={`${activeService.label} service status`}
          className="base-camp-service base-camp-service--building"
        >
          <p>This Camp service is ready.</p>
        </section>
      );
    }
    return (
      <CampProjectsPanel
        baseCamp={baseCamp}
        busyBuildingKey={busyBuildingKey}
        currentAction={currentAction}
        embedded
        error={error}
        loading={loading}
        onCancel={onCancel}
        onDeposit={onDeposit}
        onRefresh={onRefresh}
        onStart={onStart}
        travelBusy={travelBusy}
      />
    );
  })();

  return (
    <section
      aria-busy={loading || travelBusy || undefined}
      aria-labelledby="base-camp-scene-title"
      className="base-camp-scene"
      style={{
        '--base-camp-background': `url(${privateCampClearing})`,
      }}
    >
      <div aria-hidden="true" className="base-camp-scene__backdrop" />
      <header className="base-camp-scene__header">
        <div className="base-camp-scene__title">
          <p>Private base</p>
          <h1 id="base-camp-scene-title">Your Camp</h1>
        </div>
        <ConsumableEffectsHud
          serverTimeOffsetMs={baseCamp?.serverTimeOffsetMs || 0}
          user={character?.player}
          variant="camp"
        />
        <div className="base-camp-scene__header-tools">
          {character ? (
            <OperativeStatusButton
              active={activeServiceKey === 'character'}
              actionLabel="Character"
              ariaControls="base-camp-scene-content"
              className="base-camp-scene__character-status"
              currentHpPercentage={character.currentHpPercentage}
              level={character.level}
              onClick={() => selectService(services[0])}
              player={character.player}
              toggle={false}
              userMaxHp={character.userMaxHp}
              xpForNextLevel={character.xpForNextLevel}
              xpProgress={character.xpProgress}
            />
          ) : null}
          {headerMenu ? (
            <div className="base-camp-scene__menu">{headerMenu}</div>
          ) : null}
        </div>
      </header>

      <section
        aria-labelledby="base-camp-context-title"
        className="base-camp-scene__context"
      >
        {activeService.imageId ? (
          <ImageLoader
            alt=""
            className="base-camp-scene__context-image"
            imageId={activeService.imageId}
            variant={MEDIA_VARIANTS.HERO}
          />
        ) : null}
        <div className="base-camp-scene__context-copy">
          <p>{activeService.eyebrow || 'Camp service'}</p>
          <h2 id="base-camp-context-title">{activeService.label}</h2>
          <span>{activeService.description}</span>
        </div>
        <div className="base-camp-scene__context-meta">
          <strong>{contextSummary}</strong>
          {contextualProgress ? (
            <ProfessionProgress profession={contextualProgress} />
          ) : null}
        </div>
        {currentRoute ? (
          <Button
            aria-label={`Return to ${currentRoute.regionName || 'last location'} via Camp cableway`}
            className="base-camp-scene__return"
            disabled={travelBusy}
            onClick={() => onRequestRoute?.(currentRoute)}
            variant="primary"
          >
            <img alt="" src={campCablewayIcon} />
            {travelBusy
              ? 'Travelling…'
              : `Return to ${currentRoute.regionName || 'last location'}`}
          </Button>
        ) : null}
      </section>

      <div className="base-camp-scene__workspace">
        <nav aria-label="Camp navigation" className="base-camp-scene__nav">
          <div className="base-camp-scene__nav-services">
            {services.filter(service => service.unlocked).map(service => (
              <Button
                aria-controls="base-camp-scene-content"
                aria-pressed={activeServiceKey === service.key}
                key={service.key}
                onClick={() => selectService(service)}
                selected={activeServiceKey === service.key}
                variant="ghost"
              >
                <span>{service.label}</span>
              </Button>
            ))}
          </div>
          <label className="base-camp-scene__mobile-selector">
            <span>Camp section</span>
            <select
              aria-controls="base-camp-scene-content"
              aria-label="Camp section"
              onChange={event => {
                const service = services.find(candidate => (
                  candidate.key === event.target.value
                ));
                selectService(service, {
                  focusContent: false,
                  reveal: true,
                });
              }}
              value={activeServiceKey}
            >
              {services.filter(service => service.unlocked).map(service => (
                <option key={service.key} value={service.key}>
                  {service.label}
                </option>
              ))}
            </select>
          </label>
        </nav>
        <main
          aria-labelledby="base-camp-context-title"
          className="base-camp-scene__content"
          id="base-camp-scene-content"
          ref={contentRef}
          tabIndex="-1"
        >
          {loading && !baseCamp ? (
            <p className="base-camp-scene__state" role="status">
              Loading your private Camp…
            </p>
          ) : error && !baseCamp ? (
            <div className="base-camp-scene__state" role="alert">
              <p>{error}</p>
              <Button onClick={onRefresh} variant="primary">Try again</Button>
            </div>
          ) : content}
        </main>
      </div>
    </section>
  );
}
